import { useState } from 'react'
import { StatsCard, StatsContainer } from '../../styled/Stats'
import FbButton from 'components/FbUI/FbButton'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { PurchaseCreditsModal } from 'components/Modals/PurchaseCreditsModal'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import { useHasUnlimitedCredits } from '../../hooks/useHasUnlimitedCredits'
import { useFeatureFlagService } from '../../utils/useFeatureFlagService'
import { useAuth } from 'context/authentication/useAuth'
import { useCreditBalance } from '../../hooks/useCreditBalance'
import { formatInteger } from 'utils/formatting'

interface CreditBalanceCardProps {
  className?: string
}

interface CreditStat {
  name: string
  key: 'total' | 'available' | 'used' | 'pending'
  tooltip: string
}

export function CreditBalanceCard({ className = '' }: CreditBalanceCardProps) {
  const [showPurchaseModal, setShowPurchaseModal] = useState(false)
  const { user } = useAuth()
  const navigate = useNavigate()
  const hasStripeCustomerId = !!user?.company?.stripe_customer_id
  const { enableContactCredits } = useFeatureFlagService()
  const hasUnlimitedCredits = useHasUnlimitedCredits()
  const { credits, isLoading } = useCreditBalance()

  const handleBuyCreditsClick = () => {
    if (!hasStripeCustomerId) {
      toast.warning(
        'Please configure billing in settings before purchasing credits.',
        {
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          onClick: () => navigate('/settings/billing'),
        }
      )

      return
    }
    setShowPurchaseModal(true)
  }

  const creditStats: CreditStat[] = [
    {
      name: 'Credits Used',
      key: 'used' as const,
      tooltip: 'Total number of credits spent on completed contact requests',
    },
    ...(hasUnlimitedCredits
      ? []
      : [
          {
            name: 'Credits Pending',
            key: 'pending' as const,
            tooltip: 'Credits reserved for in-progress contact requests',
          },
        ]),
    ...(enableContactCredits
      ? [
          {
            name: 'Credits Remaining',
            key: 'available' as const,
            tooltip: 'Number of credits you can use for new contact requests',
          },
        ]
      : []),
  ]

  return (
    <div className={`flex flex-col relative ${className}`}>
      <StatsContainer className="mb-2">
        {creditStats.map((stat) => (
          <StatsCard key={stat.key}>
            <div className="flex justify-between w-full items-center">
              <h1>{stat.name}&nbsp;</h1>
              <HelpTooltip content={stat.tooltip} />
            </div>
            {!isLoading ? (
              <span>
                {hasUnlimitedCredits && stat.key === 'available'
                  ? '∞'
                  : formatInteger(credits[stat.key])}
              </span>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 400 }}>
                Calculating...
              </span>
            )}
          </StatsCard>
        ))}
      </StatsContainer>
      {!hasUnlimitedCredits && (
        <div className="relative z-10">
          <FbButton className="w-full" onClick={handleBuyCreditsClick}>
            Buy Credits
          </FbButton>
          <PurchaseCreditsModal
            open={showPurchaseModal}
            onClose={() => setShowPurchaseModal(false)}
          />
        </div>
      )}
    </div>
  )
}
